import { SectionList } from "../blocks/registry";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { EmptyState } from "../components/Skeletons";
import { loadPage, pageMeta, pathFromRequest } from "../lib/page";
import { loadSectionData } from "../lib/section-data";
import type { Route } from "./+types/cms-page";

/**
 * Any page the CMS resolves.
 *
 * About, the three legal pages and anything an editor publishes later all come
 * through here. There is deliberately no route file per page: a new page in the
 * CMS is live the moment it is published, and a path that resolves to nothing
 * throws a 404 rather than rendering an empty shell.
 */
export async function loader({ request }: Route.LoaderArgs) {
  const preview = new URL(request.url).searchParams.get("preview");
  const path = pathFromRequest(request);

  const page = await loadPage(path, preview);

  return {
    page,
    sectionData: await loadSectionData(page.sections),
    isPreview: Boolean(preview),
  };
}

export function meta({ loaderData, matches }: Route.MetaArgs) {
  return pageMeta(loaderData?.page, { matches });
}

export default function CmsPage({ loaderData }: Route.ComponentProps) {
  const { page, sectionData, isPreview } = loaderData;

  // The first section is usually a hero that carries its own heading, so the
  // trail only appears where the page opens with ordinary content and would
  // otherwise give no sense of position.
  const showBreadcrumbs = page.sections[0]?.sectionType !== "hero";

  return (
    <>
      {isPreview && (
        <div
          role="status"
          className="bg-gold-500 px-4 py-2 text-center text-sm font-semibold text-navy-900"
        >
          Preview — this page is not published. Only people with this link can see it.
        </div>
      )}

      <main id="main">
        {showBreadcrumbs && (
          <div className="container-site pt-8">
            <Breadcrumbs items={[{ label: page.title }]} />
            <h1 className="mt-6 text-5xl">{page.title}</h1>
          </div>
        )}

        {page.sections.length > 0 ? (
          <SectionList sections={page.sections} data={sectionData} />
        ) : (
          <div className="container-site section-y">
            <EmptyState
              title="This page has no content yet"
              description="Sections added in the CMS will appear here."
            />
          </div>
        )}
      </main>
    </>
  );
}
